import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import Modal from "../Common/Modal";

const JsQRScanner = ({ onScan, children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isModalOpen) return;

    let animationFrameId;
    let stream;

    const stopCamera = () => {
      cancelAnimationFrame(animationFrameId);
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
      }
    };

    const waitForRefs = async () => {
      // Wait until video & canvas are rendered
      while (!videoRef.current || !canvasRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) {
        console.error("❌ Canvas context not available");
        return;
      }

      const scanFrame = () => {
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            
            if (onScan) onScan(code.data);

            // ✅ Stop camera immediately and close modal
            stopCamera();
            setIsModalOpen(false);
            return;
          }
        }
        animationFrameId = requestAnimationFrame(scanFrame);
      };

      const startCamera = async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" },
          });
          video.srcObject = stream;
          video.setAttribute("playsinline", true);
          await video.play();
          requestAnimationFrame(scanFrame);
        } catch (err) {
          console.error("Camera access error:", err);
          setError("Unable to access camera");
        }
      };

      await startCamera();
    };

    waitForRefs();

    // Cleanup when modal closes or component unmounts
    return () => {
      stopCamera();
    };
  }, [isModalOpen, onScan]);

  return (
    <>
      <div onClick={() => setIsModalOpen(true)}>{children}</div>

      <Modal
        isOpen={isModalOpen}
        handleOnCloseModal={() => setIsModalOpen(false)}
        headerTitle="Scan QR Code"
        className="w-[432px] overflow-hidden hide-scrollbar shadow-xl rounded-3xl"
      >
        <div className="flex flex-col items-center justify-center py-4 space-y-3">
          <video ref={videoRef} className="rounded-lg shadow-md w-full max-w-sm" />
          <canvas ref={canvasRef} className="hidden" />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <p className="text-gray-500 text-xs text-center">
            Align the QR code within the camera frame.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default JsQRScanner;
